import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { MagneticButton } from '../components/MagneticButton';

// Palette — 3 colors only:
//   #0B0C10 obsidian, #F8F9FA paper, #E3A869 brass
const BRASS = '#E3A869';

const stagger = { hidden: { opacity: 1 }, show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } } };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };

const FeatureRow = ({ number, title, body, testId }) => (
  <motion.div
    variants={fadeUp}
    className="grid grid-cols-12 gap-4 py-8 sm:py-10 border-t border-white/10"
    data-testid={testId}
  >
    <span className="col-span-12 sm:col-span-2 text-xs tracking-[0.25em] uppercase text-[#8A8D98]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {number}
    </span>
    <h3 className="col-span-12 sm:col-span-4 text-2xl sm:text-3xl font-light tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {title}
    </h3>
    <p className="col-span-12 sm:col-span-6 text-sm sm:text-base text-[#8A8D98] leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {body}
    </p>
  </motion.div>
);

const PriceTier = ({ name, price, perks, highlight, testId }) => (
  <motion.div
    variants={fadeUp}
    className={`relative py-10 ${highlight ? 'border-t border-b' : 'border-t'} border-white/10`}
    style={highlight ? { borderColor: `${BRASS}40` } : undefined}
    data-testid={testId}
  >
    <div className="flex items-baseline justify-between mb-6">
      <h4 className="text-2xl font-light tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{name}</h4>
      {highlight && <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: BRASS }}>Most popular</span>}
    </div>
    <div className="mb-8">
      <span className="text-5xl font-light text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>${price}</span>
      <span className="text-sm text-[#8A8D98] ml-1">/ mo</span>
    </div>
    <ul className="space-y-2">
      {perks.map((p, i) => (
        <li key={i} className="text-sm text-[#D5D7DD] flex items-start gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <span style={{ color: BRASS }}>·</span>
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

export const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B0C10] text-[#F8F9FA] overflow-x-hidden" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B0C10]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-baseline gap-2" data-testid="landing-logo">
            <span className="text-xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>RealProp</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#8A8D98]">Studio</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#8A8D98]">
            <a href="#work" className="hover:text-white transition-colors" data-testid="nav-features">Work</a>
            <a href="#pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
            <Link to="/demo" className="hover:text-white transition-colors" data-testid="nav-templates">Templates</Link>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <button onClick={() => navigate('/dashboard')} className="px-4 py-2 text-sm text-white border border-white/15 rounded-full hover:border-white/30 transition-colors" data-testid="dashboard-button">
                Dashboard
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="hidden sm:block px-3 py-2 text-sm text-[#D5D7DD] hover:text-white transition-colors" data-testid="login-button">
                  Sign in
                </button>
                <MagneticButton onClick={() => navigate('/signup')} className="px-4 sm:px-5 py-2 rounded-full text-sm font-medium text-[#0B0C10] hover:opacity-90 transition-opacity" style={{ background: BRASS }} data-testid="signup-button">
                  Get started
                </MagneticButton>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero — pure typography, no imagery */}
      <section className="relative pt-44 sm:pt-56 pb-24 sm:pb-40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp} className="text-[10px] uppercase tracking-[0.3em] text-[#8A8D98] mb-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span style={{ color: BRASS }}>●</span> &nbsp;Marketing studio for real estate
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="text-[44px] sm:text-7xl lg:text-[112px] leading-[0.95] tracking-tight font-light max-w-5xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Turn any listing into <br className="hidden sm:block" />
              <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic', color: BRASS }}>social magic.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-10 max-w-xl text-base sm:text-lg text-[#8A8D98] leading-relaxed">
              Paste a Zillow link. Receive posts, stories, reels, flyers, and email blasts — auto-filled with your photos, on your brand, ready to publish.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-12 flex flex-col sm:flex-row gap-3 sm:items-center">
              <MagneticButton
                onClick={() => navigate(user ? '/magic-create' : '/signup')}
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium text-[#0B0C10] hover:opacity-90 transition-opacity"
                style={{ background: BRASS }}
                data-testid="cta-magic-import"
              >
                Try Magic Import
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </MagneticButton>
              <Link
                to="/demo"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm text-white hover:text-white/80 transition-colors group"
                data-testid="cta-view-templates"
              >
                View templates
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features — minimal line list, no cards */}
      <section id="work" className="relative max-w-6xl mx-auto px-6 sm:px-10 pb-24 sm:pb-32">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="mb-14 sm:mb-20 flex items-end justify-between flex-wrap gap-6">
            <h2 className="text-3xl sm:text-5xl tracking-tight font-light max-w-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Every format. <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>One click.</span>
            </h2>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#8A8D98]">{`/ 06`}</span>
          </motion.div>
          <FeatureRow number="01" title="Magic Import" body="Paste a listing URL. We pull photos, price, beds, baths, description — and apply your brand kit in seconds." testId="feature-magic-import" />
          <FeatureRow number="02" title="Brand Kit" body="Your colors, fonts and logo applied automatically across every template you generate." testId="feature-brand-kit" />
          <FeatureRow number="03" title="Multi-format" body="Posts, stories, carousels, flyers, postcards — generated together so you publish once and post everywhere." testId="feature-multi-format" />
          <FeatureRow number="04" title="Hybrid Reels" body="Auto-cut reels with music, voiceover and motion graphics." testId="feature-reels" />
          <FeatureRow number="05" title="Newsletters" body="Drag-and-drop emails with auto-filled property cards and one-tap send." testId="feature-newsletter" />
          <FeatureRow number="06" title="Auto-publish" body="Send straight to Facebook and Instagram with one click." testId="feature-publish" />
          <div className="border-t border-white/10" />
        </motion.div>
      </section>

      {/* Pricing — clean rows */}
      <section id="pricing" className="relative max-w-6xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="mb-14 sm:mb-20">
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#8A8D98]">Pricing</span>
            <h2 className="mt-3 text-3xl sm:text-5xl tracking-tight font-light" style={{ fontFamily: 'Outfit, sans-serif' }}>
              For solo agents <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>and</span> teams.
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10">
            <PriceTier name="Starter" price="19" perks={['10 scrapes / mo', 'All template formats', '1 brand kit']} testId="pricing-starter" />
            <PriceTier name="Pro" price="39" perks={['Unlimited scrapes', 'Auto-publish to Meta', 'Print center access', 'Priority AI']} highlight testId="pricing-pro" />
            <PriceTier name="Team" price="149" perks={['Everything in Pro', 'Up to 10 seats', 'Shared brand kits', 'Admin controls']} testId="pricing-team" />
          </div>
          <div className="border-t border-white/10 mt-10 pt-10 text-center">
            <Link to="/subscription" className="text-sm text-[#8A8D98] hover:text-white transition-colors inline-flex items-center gap-1" data-testid="see-full-pricing">
              See full pricing
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Final CTA — typographic, no card */}
      <section className="relative max-w-6xl mx-auto px-6 sm:px-10 py-24 sm:py-40 text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
          <h3 className="text-4xl sm:text-7xl lg:text-8xl tracking-tight font-light leading-[0.95]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ten seconds <br /> from listing to <br />
            <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic', color: BRASS }}>published.</span>
          </h3>
          <MagneticButton
            onClick={() => navigate(user ? '/magic-create' : '/signup')}
            className="mt-12 inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-medium text-[#0B0C10] hover:opacity-90 transition-opacity"
            style={{ background: BRASS }}
            data-testid="cta-final"
          >
            Start free — no card needed
            <ArrowRight className="h-4 w-4" />
          </MagneticButton>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-10">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-base font-semibold tracking-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>RealProp</div>
            <p className="text-[11px] text-[#8A8D98] mt-1">© 2026 · Marketing on autopilot.</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-[#8A8D98]">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
            <Link to="/data-deletion" className="hover:text-white transition-colors" data-testid="footer-data-deletion-link">Data Deletion</Link>
            <a href="mailto:support@realprop.io" className="hover:text-white transition-colors" data-testid="footer-support-link">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
